import * as React from "react";

import { cn } from "../../lib/utils";

const Pagination = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, children, ...props }, ref) => (
    <nav ref={ref} aria-label="pagination" className={cn("flex", className)} {...props}>
      <ul className="flex items-center -space-x-px">{children}</ul>
    </nav>
  ),
);
Pagination.displayName = "Pagination";

interface PaginationItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

const PaginationItem = React.forwardRef<HTMLButtonElement, PaginationItemProps>(
  ({ className, active, children, ...props }, ref) => (
    <li>
      <button
        ref={ref}
        type="button"
        aria-current={active ? "page" : undefined}
        className={cn(
          "inline-flex h-10 min-w-10 items-center justify-center border border-input px-3 text-sm font-medium ring-offset-background transition-colors first:rounded-l-md last:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          active
            ? "z-10 bg-primary text-primary-foreground"
            : "bg-background hover:bg-accent hover:text-accent-foreground",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    </li>
  ),
);
PaginationItem.displayName = "PaginationItem";

const makeControl = (label: string, glyph: string) => {
  const Control = React.forwardRef<HTMLButtonElement, Omit<PaginationItemProps, "children">>(
    (props, ref) => (
      <PaginationItem ref={ref} aria-label={label} {...props}>
        {glyph}
      </PaginationItem>
    ),
  );
  Control.displayName = `Pagination${label}`;
  return Control;
};

const PaginationFirst = makeControl("First", "«");
const PaginationPrev = makeControl("Prev", "‹");
const PaginationNext = makeControl("Next", "›");
const PaginationLast = makeControl("Last", "»");

// Non-interactive gap marker rendered between page-number groups.
const PaginationEllipsis = ({ className }: { className?: string }) => (
  <li>
    <span
      aria-hidden
      className={cn(
        "inline-flex h-10 min-w-10 items-center justify-center border border-input bg-background px-3 text-sm text-muted-foreground",
        className,
      )}
    >
      …
    </span>
  </li>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export type PaginationRangeItem = number | "ellipsis";

const range = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(end - start + 1, 0) }, (_, i) => start + i);

// Returns the page numbers to render, collapsing long runs to "ellipsis".
// Shows `boundaryCount` pages at each end plus `siblingCount` pages on each
// side of the current page. (MUI usePagination algorithm.)
function getPaginationRange(
  currentPage: number,
  totalPages: number,
  { siblingCount = 1, boundaryCount = 2 }: { siblingCount?: number; boundaryCount?: number } = {},
): PaginationRangeItem[] {
  const totalNumbers = boundaryCount * 2 + siblingCount * 2 + 3;
  if (totalPages <= totalNumbers) {
    return range(1, totalPages);
  }

  const startPages = range(1, boundaryCount);
  const endPages = range(totalPages - boundaryCount + 1, totalPages);

  const siblingsStart = Math.max(
    Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2,
  );
  const siblingsEnd = Math.min(
    Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
    endPages[0] - 2,
  );

  return [
    ...startPages,
    ...(siblingsStart > boundaryCount + 2
      ? (["ellipsis"] as PaginationRangeItem[])
      : boundaryCount + 1 < totalPages - boundaryCount
        ? [boundaryCount + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < totalPages - boundaryCount - 1
      ? (["ellipsis"] as PaginationRangeItem[])
      : totalPages - boundaryCount > boundaryCount
        ? [totalPages - boundaryCount]
        : []),
    ...endPages,
  ];
}

export {
  Pagination,
  PaginationItem,
  PaginationFirst,
  PaginationPrev,
  PaginationNext,
  PaginationLast,
  PaginationEllipsis,
  getPaginationRange,
};
